'use client';

import { useState, Suspense } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { CommunityRating } from '@alltrails/reviews/types/CommunityRating';
import Link from '@alltrails/denali/components/Link';
import Toast from '@alltrails/denali/components/Toast';
import CheckCircleFilled from '@alltrails/denali/icons/CheckCircleFilled';
import { DifficultyIcon, getTrailDifficultyFromNumber, getTrailDifficultyLabel } from '@alltrails/trails';
import IconButton from '@alltrails/denali/components/IconButton';
import EditIcon from '@alltrails/denali/icons/Edit';
import ErrorIcon from '@alltrails/denali/icons/Error';
import logTrailDetailsDifficultyTapped from '@alltrails/amplitude/events/logTrailDetailsDifficultyTapped';
import styles from './Difficulty.module.scss';
import useTrailEditToolbar from '@/hooks/useTrailEditToolbar';

const DifficultyModal = dynamic(() => import('./DifficultyModal'), { ssr: false });

type DifficultyProps = {
  difficulty: number;
  isAdminOrPLPUser?: boolean;
  communityRating?: CommunityRating;
  trailId: number;
};

const Difficulty = ({ difficulty, isAdminOrPLPUser, communityRating, trailId }: DifficultyProps) => {
  const [isDifficultyModalOpen, setIsDifficultyModalOpen] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const { openTrailEditToolbar } = useTrailEditToolbar();
  const intl = useIntl();
  const trailDifficulty = getTrailDifficultyFromNumber(difficulty);
  const difficultyText = getTrailDifficultyLabel(intl, trailDifficulty);

  const handleSubmitSuccess = () => {
    setShowSuccessToast(true);
    setTimeout(() => {
      setShowSuccessToast(false);
    }, 3000);
  };

  const handleSubmitError = () => {
    setShowErrorToast(true);
    setTimeout(() => {
      setShowErrorToast(false);
    }, 3000);
  };

  return (
    <>
      <span data-testid="trail-difficulty" className={classNames({ [styles.adminDifficulty]: isAdminOrPLPUser })}>
        <div className={styles.difficultyContainerWithIcon}>
          <div className={styles.difficultyIcon}>
            <DifficultyIcon difficulty={difficulty} />
          </div>
          <Link
            className={styles.difficultyLink}
            onClick={() => {
              logTrailDetailsDifficultyTapped({
                trail_difficulty: trailDifficulty,
                trail_id: trailId
              });
              window.Sprig?.('identifyAndTrack', {
                eventName: 'trail_details_difficulty_tapped'
              });
              setIsDifficultyModalOpen(true);
            }}
            variant="primary"
            testId="difficulty-level-button"
            title={difficultyText}
          >
            {difficultyText}
          </Link>
        </div>
        {isAdminOrPLPUser && (
          <IconButton
            icon={{ Component: EditIcon }}
            className={styles.editIcon}
            testId="edit-icon"
            variant="primary"
            onClick={() => openTrailEditToolbar()}
            size="sm"
            title={intl.formatMessage({ defaultMessage: 'Edit' })}
          />
        )}
      </span>
      {isDifficultyModalOpen && (
        <Suspense fallback={null}>
          <DifficultyModal
            isOpen={isDifficultyModalOpen}
            onClose={() => setIsDifficultyModalOpen(false)}
            difficulty={difficulty}
            communityRating={communityRating}
            trailId={trailId}
            onSubmitSuccess={handleSubmitSuccess}
            onSubmitError={handleSubmitError}
          />
        </Suspense>
      )}
      <Toast
        isOpen={showSuccessToast}
        icon={{ Component: CheckCircleFilled }}
        message={<FormattedMessage defaultMessage="Thanks for your feedback!" />}
        position="alternative"
        type="neutral"
        testId="difficulty-feedback-toast"
        className={styles.toast}
      />
      <Toast
        isOpen={showErrorToast}
        icon={{ Component: ErrorIcon }}
        message={<FormattedMessage defaultMessage="Unable to submit rating" />}
        position="alternative"
        type="error"
        testId="difficulty-error-toast"
        className={styles.toast}
      />
    </>
  );
};

export default Difficulty;
